import { useEffect, useRef, useState, type ButtonHTMLAttributes, type ReactElement } from 'react';

import { motion, AnimatePresence } from 'framer-motion';
import { z } from 'zod';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Dialog,
  DialogFooter,
} from '@/components/ui/Dialog';
import { ResponsiveDialogContent } from '@/components/ui/ResponsiveDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { MoreHorizontal, Trash2 } from 'lucide-react';
import {
  ResponsiveDialogHeader,
  ResponsiveDialogHeaderActionButton,
  ResponsiveDialogHeaderActions,
  ResponsiveDialogHeaderCloseButton,
} from '@/components/ui/ResponsiveDialogHeader';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { SafeHtml } from '@/components/editor/SafeHtml';
import {
  TaskBillingControl,
  TaskDateRangeControl,
  TaskDurationControl,
  TaskHourlyRateControl,
  TaskMembersControl,
  TaskProjectControl,
  type TaskControlId,
} from './TaskEditorControls';
import { TaskDetailsSummary, type TaskDetailsSummaryItem } from './TaskDetailsSummary';

export interface ProjectOption {
  id: string;
  name: string;
  color?: string | null;
  hourlyRate?: number | null;
  currency?: 'BRL' | 'USD';
}

export interface MemberOption {
  id: string;
  name: string;
}

export interface TaskFormData {
  id?: string;
  title: string;
  description: string;
  duration: string;
  projectId: string;
  date: string;
  endDate?: string;
  hourlyRate: number;
  currency: 'BRL' | 'USD';
  billingStatus: 'unbilled' | 'billed';
  // 0, 1 or 2 members (issues #8 and #33). Empty = unassigned task, shown
  // in the timeline's Unassigned row until someone attaches a member.
  memberIds: string[];
}

interface TaskDialogProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskFormData | null;
  projects: ProjectOption[];
  members: MemberOption[];
  /**
   * Which screen to show when the dialog opens.
   * - 'auto': edit when creating a new task, view when editing an existing one
   * - 'view': always start in read-only "Task Details"
   * - 'edit': always start in the editable form (pre-filled if task is provided)
   */
  initialMode?: 'auto' | 'view' | 'edit';
  /**
   * The id of the currently authenticated viewer. Used to default the
   * member selection to "self" on a fresh task — see issue #8.
   */
  currentUserId?: string;
  onSave: (data: TaskFormData) => void;
  /** Persist a contextual change without leaving Task Details open. */
  onInlineSave?: (data: TaskFormData) => void;
  onDelete?: (id: string) => void;
}

function computeInitialViewMode(
  mode: 'auto' | 'view' | 'edit',
  task: TaskFormData | null,
): boolean {
  if (mode === 'view') return true;
  if (mode === 'edit') return false;
  // 'auto': view when editing an existing task, edit when creating new.
  return Boolean(task?.id);
}

const formSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().max(50000, 'Description is too long'),
  duration: z.string().min(1, 'Duration is required'),
  // Empty string — issue #10. A brand-new workspace with no projects can
  // still log entries. The Select shows "No project" as an explicit option
  // with the empty-string value, so the field is always a string.
  projectId: z.string(),
  date: z.string().min(1, 'Start date is required'),
  endDate: z.string().optional(),
  hourlyRate: z.number().min(0, 'Hourly rate cannot be negative'),
  currency: z.enum(['BRL', 'USD']),
  billingStatus: z.enum(['unbilled', 'billed']),
  // 0, 1 or 2 members (issues #8 and #33). Zero = unassigned: a task is not
  // always attached to a member at creation time — we attach it later, and
  // until then it shows up in the timeline's Unassigned row. The server
  // defaults to the current user only when `memberIds` is omitted entirely.
  memberIds: z
    .array(z.string().min(1))
    .max(2, 'A task can be assigned to at most 2 members'),
});

type FormValues = z.infer<typeof formSchema>;

// Keep the title field's focus treatment identical in the create form and
// the inline editor used by Task Details. The create form keeps the Input's
// normal border/padding so the field remains discoverable when unfocused;
// only the focus treatment is shared with the borderless inline editor.
const TASK_TITLE_INPUT_FOCUS_CLASS =
  'focus:!pl-3 focus:rounded-md focus:border-primary/40 focus:bg-background focus:shadow-sm focus:ring-2 focus:ring-primary/20 focus:ring-offset-0 focus-visible:!pl-3 focus-visible:rounded-md focus-visible:border-primary/40 focus-visible:bg-background focus-visible:shadow-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:!ring-offset-0';

const TASK_TITLE_INPUT_NO_FOCUS_RING_CLASS =
  'focus:!border-input focus:!outline-none focus:!ring-0 focus:!ring-offset-0 focus-visible:!border-input focus-visible:!outline-none focus-visible:!ring-0 focus-visible:!ring-offset-0';

type PersistedTaskState = Pick<
  TaskFormData,
  | 'title'
  | 'description'
  | 'duration'
  | 'projectId'
  | 'date'
  | 'endDate'
  | 'hourlyRate'
  | 'currency'
  | 'billingStatus'
  | 'memberIds'
>;

function toPersistedTaskState(data: PersistedTaskState): PersistedTaskState {
  return {
    title: data.title,
    description: data.description,
    duration: data.duration,
    projectId: data.projectId,
    date: data.date,
    endDate: data.endDate || undefined,
    hourlyRate: data.hourlyRate,
    currency: data.currency,
    billingStatus: data.billingStatus,
    memberIds: [...data.memberIds],
  };
}

function hasSamePersistedTaskState(a: PersistedTaskState, b: PersistedTaskState): boolean {
  return (
    a.title === b.title &&
    a.description === b.description &&
    a.duration === b.duration &&
    a.projectId === b.projectId &&
    a.date === b.date &&
    a.endDate === b.endDate &&
    a.hourlyRate === b.hourlyRate &&
    a.currency === b.currency &&
    a.billingStatus === b.billingStatus &&
    a.memberIds.length === b.memberIds.length &&
    a.memberIds.every((memberId, index) => memberId === b.memberIds[index])
  );
}

export function TaskDialog({
  isOpen,
  onClose,
  task,
  projects,
  members,
  initialMode = 'auto',
  currentUserId,
  onSave,
  onInlineSave,
  onDelete,
}: TaskDialogProps) {
  const isEditing = !!task?.id;
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: task?.title ?? '',
      description: task?.description ?? '',
      duration: task?.duration ?? '1h',
      projectId: task?.projectId ?? '',
      date: task?.date ?? format(new Date(), 'yyyy-MM-dd'),
      endDate: task?.endDate ?? '',
      hourlyRate: task?.hourlyRate ?? 0,
      currency: task?.currency ?? 'BRL',
      billingStatus: task?.billingStatus ?? 'billed',
      memberIds: task?.memberIds ?? (currentUserId ? [currentUserId] : []),
    },
  });

  const memberIds = watch('memberIds') ?? [];

  const [openPanel, setOpenPanel] = useState<TaskControlId | null>(null);
  const [isViewMode, setIsViewMode] = useState(() => computeInitialViewMode(initialMode, task));
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionBeforeEdit, setDescriptionBeforeEdit] = useState('');
  const persistedTaskStateRef = useRef<PersistedTaskState | null>(null);

  // Reset mode + form values whenever the dialog opens or the task changes.
  // Without this, switching from one task to another in the same dialog
  // instance would show stale values from the previous task.
  useEffect(() => {
    if (!isOpen) return;
    setIsViewMode(computeInitialViewMode(initialMode, task));
    const initialValues: FormValues = {
      title: task?.title ?? '',
      description: task?.description ?? '',
      duration: task?.duration ?? '1h',
      projectId: task?.projectId ?? '',
      date: task?.date ?? format(new Date(), 'yyyy-MM-dd'),
      endDate: task?.endDate ?? '',
      hourlyRate: task?.hourlyRate ?? 0,
      currency: task?.currency ?? 'BRL',
      billingStatus: task?.billingStatus ?? 'billed',
      memberIds: task?.memberIds ?? (currentUserId ? [currentUserId] : []),
    };
    reset(initialValues);
    persistedTaskStateRef.current = task?.id ? toPersistedTaskState(initialValues) : null;
    setOpenPanel(null);
    setIsEditingDescription(false);
  }, [task?.id, task?.title, task?.description, task?.duration, task?.projectId, task?.date, task?.endDate, task?.hourlyRate, task?.currency, task?.billingStatus, task?.memberIds, initialMode, isOpen, reset, currentUserId]);

  useEffect(() => {
    if (!isOpen) setOpenPanel(null);
  }, [isOpen]);

  const saveInline = (overrides: Partial<FormValues> = {}) => {
    if (!task?.id) return;
    const values = getValues();
    const data: TaskFormData = {
      id: task.id,
      ...values,
      ...overrides,
      endDate: (overrides.endDate ?? values.endDate) || undefined,
    };
    const nextState = toPersistedTaskState(data);
    const persistedState = persistedTaskStateRef.current ?? toPersistedTaskState(task);
    if (hasSamePersistedTaskState(nextState, persistedState)) return;

    persistedTaskStateRef.current = nextState;
    (onInlineSave ?? onSave)(data);
  };

  const handlePanelOpenChange = (panel: TaskControlId, open: boolean) => {
    if (!open && isViewMode && openPanel === panel) {
      saveInline();
    }
    setOpenPanel(open ? panel : null);
  };

  const toggleMember = (id: string) => {
    const next = memberIds.includes(id)
      ? memberIds.filter((m) => m !== id)
      : memberIds.length >= 2
        ? memberIds // cap at 2 — ignore additional picks
        : [...memberIds, id];
    setValue('memberIds', next, { shouldValidate: true, shouldDirty: true });
    if (isViewMode && next !== memberIds) saveInline({ memberIds: next });
  };

  const selectProject = (projectId: string) => {
    setValue('projectId', projectId, { shouldValidate: true, shouldDirty: true });
    const selectedProject = projects.find((project) => project.id === projectId);
    setValue('hourlyRate', selectedProject?.hourlyRate ?? 0, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('currency', selectedProject?.currency ?? 'BRL', {
      shouldDirty: true,
      shouldValidate: true,
    });
    if (isViewMode) {
      saveInline({
        projectId,
        hourlyRate: selectedProject?.hourlyRate ?? 0,
        currency: selectedProject?.currency ?? 'BRL',
      });
    }
  };

  const useCurrentProjectRate = () => {
    const selectedProject = projects.find((project) => project.id === watch('projectId'));
    setValue('hourlyRate', selectedProject?.hourlyRate ?? 0, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('currency', selectedProject?.currency ?? 'BRL', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const onSubmit = (values: FormValues) => {
    // A new task without an explicit end date is a same-day task by default.
    // Existing tasks keep their optional end date untouched when edited.
    const endDate = task?.id ? values.endDate : values.endDate || values.date;
    const data: TaskFormData = {
      id: task?.id,
      ...values,
      endDate: endDate || undefined,
    };
    if (task?.id) {
      const nextState = toPersistedTaskState(data);
      const persistedState = persistedTaskStateRef.current ?? toPersistedTaskState(task);
      if (hasSamePersistedTaskState(nextState, persistedState)) return;
      persistedTaskStateRef.current = nextState;
    }
    onSave(data);
    // NOTE: no reset() here. handleSave keeps the dialog OPEN on error so
    // the user can fix and retry — resetting would wipe the values they
    // just typed. The useEffect on isOpen already resets the form the next
    // time the dialog opens.
  };

  const commitTitleEdit = () => {
    saveInline();
  };

  const beginDescriptionEdit = () => {
    setDescriptionBeforeEdit(watch('description'));
    setIsEditingDescription(true);
  };

  const cancelDescriptionEdit = () => {
    setValue('description', descriptionBeforeEdit);
    setIsEditingDescription(false);
  };

  const commitDescriptionEdit = () => {
    setIsEditingDescription(false);
    saveInline();
  };

  const renderTaskSummaryItem = (
    item: TaskDetailsSummaryItem,
    trigger: ReactElement<ButtonHTMLAttributes<HTMLButtonElement>>,
  ) => {
    switch (item.id) {
      case 'project':
        return (
          <TaskProjectControl
            value={watch('projectId') ?? ''}
            projects={projects}
            open={openPanel === 'project'}
            error={errors.projectId?.message as string | undefined}
            trigger={trigger}
            onOpenChange={(open) => handlePanelOpenChange('project', open)}
            onChange={selectProject}
          />
        );
      case 'members':
        return (
          <TaskMembersControl
            value={memberIds}
            members={members}
            open={openPanel === 'members'}
            error={errors.memberIds?.message as string | undefined}
            trigger={trigger}
            onOpenChange={(open) => handlePanelOpenChange('members', open)}
            onToggle={toggleMember}
          />
        );
      case 'hourly-rate':
        return (
          <TaskHourlyRateControl
            value={watch('hourlyRate') ?? 0}
            currency={watch('currency')}
            project={projects.find((project) => project.id === watch('projectId'))}
            open={openPanel === 'hourly-rate'}
            error={errors.hourlyRate?.message}
            trigger={trigger}
            onOpenChange={(open) => handlePanelOpenChange('hourly-rate', open)}
            onRateChange={(value) =>
              setValue('hourlyRate', value as number, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onCurrencyChange={(currency) =>
              setValue('currency', currency, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onUseProjectRate={useCurrentProjectRate}
          />
        );
      case 'duration':
        return (
          <TaskDurationControl
            value={watch('duration')}
            open={openPanel === 'duration'}
            error={errors.duration?.message as string | undefined}
            trigger={trigger}
            onOpenChange={(open) => handlePanelOpenChange('duration', open)}
            onChange={(value) =>
              setValue('duration', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        );
      case 'dates':
        return (
          <TaskDateRangeControl
            startDate={watch('date')}
            endDate={watch('endDate')}
            open={openPanel === 'dates'}
            error={errors.date?.message as string | undefined}
            trigger={trigger}
            onOpenChange={(open) => handlePanelOpenChange('dates', open)}
            onStartDateChange={(date) =>
              setValue('date', date, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onEndDateChange={(date) =>
              setValue('endDate', date, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        );
      case 'billing':
        return (
          <TaskBillingControl
            value={watch('billingStatus')}
            trigger={trigger}
            onChange={(billingStatus) => {
              setValue('billingStatus', billingStatus, {
                shouldDirty: true,
                shouldValidate: true,
              });
              if (isViewMode) saveInline({ billingStatus });
            }}
          />
        );
    }
  };

  const viewTitleEditor = (
    <>
      <span className="sr-only">{watch('title')}</span>
      <Input
        aria-label="Task title"
        data-testid="task-details-title-input"
        value={watch('title')}
        onChange={(event) => setValue('title', event.target.value, { shouldDirty: true })}
        onBlur={commitTitleEdit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitTitleEdit();
            event.currentTarget.blur();
          }
          if (event.key === 'Escape') {
            event.preventDefault();
            setValue('title', persistedTaskStateRef.current?.title ?? task?.title ?? '');
            event.currentTarget.blur();
          }
        }}
        className={`pointer-events-auto h-auto min-h-9 w-[calc(100%-7rem)] border-0 bg-transparent !p-0 text-center text-base font-semibold shadow-none ${TASK_TITLE_INPUT_FOCUS_CLASS} sm:w-full sm:text-left sm:text-lg`}
      />
    </>
  );

  const taskSummary = (
    <TaskDetailsSummary
      projectId={watch('projectId') ?? ''}
      memberIds={memberIds}
      date={watch('date')}
      endDate={watch('endDate')}
      duration={watch('duration')}
      hourlyRate={watch('hourlyRate') ?? 0}
      currency={watch('currency')}
      billingStatus={watch('billingStatus')}
      projects={projects}
      members={members}
      renderItem={renderTaskSummaryItem}
    />
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()} modal={false}>
      <ResponsiveDialogContent
        data-testid="task-dialog"
        className="sm:!max-w-[704px] sm:!grid-rows-[auto_minmax(0,1fr)]"
        showCloseButton={false}
        onPointerDownOutside={(e) => {
          // Keep the dialog open when the user clicks inside a popover portal
          // (date picker, select dropdown) rendered outside the dialog's DOM.
          const target = e.target as HTMLElement | null;
          if (target?.closest('[data-radix-popper-content-wrapper]')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          // Same guard for keyboard/focus interactions.
          const target = e.target as HTMLElement | null;
          if (target?.closest('[data-radix-popper-content-wrapper]')) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (!openPanel) return;
          e.preventDefault();
          e.stopPropagation();
          handlePanelOpenChange(openPanel, false);
        }}
      >
        <ResponsiveDialogHeader
          testId="task-dialog-header"
          titleAccessibleName={isEditing && isViewMode ? watch('title') : undefined}
          title={
            isEditing
              ? isViewMode
                ? viewTitleEditor
                : 'Edit Task'
              : 'New Task'
          }
          onBack={onClose}
          showRightOnDesktop
          right={
            <TaskDialogHeaderActions
              showMenu={Boolean(isViewMode && task?.id && onDelete)}
              onDelete={task?.id && onDelete ? () => onDelete(task.id!) : undefined}
            />
          }
        />

        <div
          data-testid="task-dialog-body"
          className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:overflow-y-auto sm:px-0 sm:py-0"
        >
          <AnimatePresence mode="wait">
          {isViewMode ? (
            <motion.div
              key="view"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="min-h-full space-y-6 pt-1 sm:pt-1"
            >
              <div className="space-y-5">{taskSummary}</div>

              <section
                aria-labelledby="task-details-description-label"
                data-testid="task-details-description"
                className="border-t pt-5"
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 id="task-details-description-label" className="text-sm font-semibold">
                    Description
                  </h3>
                  {!isEditingDescription && (
                    <span className="text-xs text-muted-foreground">Click to edit</span>
                  )}
                </div>
                {isEditingDescription ? (
                  <div data-testid="task-details-description-editor" className="space-y-3">
                    <div className="max-h-[48vh] overflow-y-auto rounded-md">
                      <RichTextEditor
                        value={watch('description') ?? ''}
                        onChange={(value) => setValue('description', value, { shouldDirty: true })}
                        placeholder="Describe the task (optional)"
                        minHeight="200px"
                      />
                    </div>
                    <div className="flex flex-row gap-3 [&>button]:min-w-0 [&>button]:flex-1">
                      <Button type="button" variant="outline" onClick={cancelDescriptionEdit}>
                        Cancel
                      </Button>
                      <Button type="button" onClick={commitDescriptionEdit}>
                        Save description
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Edit description"
                    onClick={beginDescriptionEdit}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        beginDescriptionEdit();
                      }
                    }}
                    className="min-h-[220px] max-h-[48vh] cursor-text overflow-y-auto rounded-lg border bg-muted/20 p-5 outline-none transition-colors hover:border-primary/50 focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <SafeHtml
                      html={watch('description')}
                      className="text-foreground"
                      fallback="No description provided."
                    />
                  </div>
                )}
              </section>

            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              onSubmit={handleSubmit(onSubmit)}
              className="flex min-h-full flex-col space-y-5"
            >
              <div className="space-y-2">
                <Label htmlFor="title">Task name</Label>
                <Input
                  id="title"
                  aria-label="Title"
                  className={TASK_TITLE_INPUT_NO_FOCUS_RING_CLASS}
                  {...register('title')}
                />
                {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
              </div>

              <div className="space-y-5">{taskSummary}</div>

              <section
                aria-labelledby="task-details-description-label"
                data-testid="task-details-description"
                className="border-t pt-5"
              >
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 id="task-details-description-label" className="text-sm font-semibold">
                    Description
                  </h3>
                </div>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <RichTextEditor
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      placeholder="Describe the task (optional)"
                      minHeight="120px"
                    />
                  )}
                />
                {errors.description && (
                  <p className="text-sm text-destructive">{errors.description.message}</p>
                )}
              </section>

              <DialogFooter
                data-testid="task-dialog-footer"
                className="sticky bottom-0 z-10 mt-auto flex-row gap-3 border-t bg-background py-4 [&>button]:min-w-0 [&>button]:flex-1 sm:mt-0 sm:py-4"
              >
                {isEditing && (
                  <Button type="button" variant="outline" className="flex-1" onClick={() => setIsViewMode(true)}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" className="flex-1">{isEditing ? 'Save Changes' : 'Create Task'}</Button>
              </DialogFooter>
            </motion.form>
          )}
          </AnimatePresence>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
}

function TaskDialogHeaderActions({
  showMenu,
  onDelete,
}: {
  showMenu: boolean;
  onDelete?: () => void;
}) {
  return (
    <ResponsiveDialogHeaderActions testId="task-dialog-header-actions">
      {showMenu && onDelete ? <TaskActionsMenu onDelete={onDelete} /> : null}
      <ResponsiveDialogHeaderCloseButton className="hidden sm:inline-flex" />
    </ResponsiveDialogHeaderActions>
  );
}

function TaskActionsMenu({ onDelete }: { onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <ResponsiveDialogHeaderActionButton
          aria-label="Task actions"
        >
          <MoreHorizontal className="h-5 w-5" aria-hidden="true" />
        </ResponsiveDialogHeaderActionButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onSelect={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
          Delete task
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
